import {
  ClassDetailsQuery_ActivityFragment,
  ClassDetailsSectionsQuery as ClassDetailsSectionsQueryType,
  ClassDetailsSectionsQueryVariables,
  SectionFilter,
} from "@outschool/gql-frontend-generated";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { getEsaWaitPeriod, useSession } from "@outschool/ui-auth";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { ROWS_PER_PAGE } from "./AvailableTimes/DesktopSectionTable";
import { MOBILE_ROWS_PER_PAGE } from "./AvailableTimes/MobileSectionTable";
import { ClassDetailsQuery_Section } from "./ClassDetailsQuery";

const ClassDetailsSections_Activity = gql`
  fragment ClassDetailsSections_Activity on Activity {
    uid
    is_ongoing_weekly
    paginatedFilteredSections(
      filter: $sectionFilter
      esaBlackoutLengthDays: $esaBlackoutLengthDays
      offset: $offset
      limit: $limit
    ) {
      totalCount
      data {
        ...ClassDetailsQuery_Section
        __typename
      }
    }
  }
  ${ClassDetailsQuery_Section}
`;

export const ClassDetailsSectionsQuery = gql`
  query ClassDetailsSections(
    $uid: ID
    $slugId: String
    $sectionFilter: SectionFilter
    $esaBlackoutLengthDays: Int
    $offset: Int
    $limit: Int
  ) {
    activity(uid: $uid, slugId: $slugId) {
      ...ClassDetailsSections_Activity
      __typename
    }
  }
  ${ClassDetailsSections_Activity}
`;

interface UseClassDetailsSectionsProps {
  isLoading: boolean;
  paginatedFilteredSections?: ClassDetailsSectionsQueryType["activity"]["paginatedFilteredSections"];
  fetchMore: any;
}

export default function useClassDetailsSections({
  activityWithoutSections,
  skip = false,
  limitOverride = undefined,
}: {
  activityWithoutSections?: ClassDetailsQuery_ActivityFragment;
  skip?: boolean;
  limitOverride?: number;
}): UseClassDetailsSectionsProps {
  const { isEsaSession } = useSession();
  const esaBlackoutLengthDays = isEsaSession ? getEsaWaitPeriod() : null;
  const sectionFilter = React.useMemo(() => {
    if (activityWithoutSections?.allowWaitlist) {
      return SectionFilter.BookableOrSoldOut;
    }
    return SectionFilter.Bookable;
  }, [activityWithoutSections?.allowWaitlist]);

  const isMobile = useIsMobile();

  const limit =
    limitOverride ?? (isMobile ? MOBILE_ROWS_PER_PAGE : ROWS_PER_PAGE);

  const args = {
    variables: {
      uid: activityWithoutSections?.uid,
      sectionFilter,
      esaBlackoutLengthDays: esaBlackoutLengthDays,
      offset: 0,
      limit,
    },
    skip: skip || !activityWithoutSections?.uid,
    ssr: false,
  };

  const { data, loading, fetchMore } = useQueryWithPreviousData<
    ClassDetailsSectionsQueryType,
    ClassDetailsSectionsQueryVariables
  >(ClassDetailsSectionsQuery, args);

  const activity = data?.activity;

  return {
    isLoading: loading && !!activityWithoutSections?.uid,
    paginatedFilteredSections: activity?.paginatedFilteredSections,
    fetchMore,
  };
}
